import React, { useState, useEffect } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addDays from 'date-fns/addDays';
import { FaChevronLeft, FaChevronRight, FaPlus, FaTimes, FaEdit, FaCompass, FaSearch } from 'react-icons/fa';
import { collection, query, where, orderBy, getDocs, addDoc, serverTimestamp, limit, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { fetchGoogleCalendarEvents, createGoogleCalendarEvent, updateGoogleCalendarEvent } from '../services/googleCalendarService';
import { getUserIntegrationsStatus } from '../services/userIntegrationService';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import PortalBackground from '../components/PortalBackground';
import { isActiveClientAccount } from '../utils/accountLifecycle'; // one client-lifecycle rule
import { idTokenHeader } from '../services/authHeaders';

// Custom CSS for dark mode calendar
const calendarDarkModeStyles = `
  .dark .rbc-calendar {
    background-color: #1f2937;
    color: #f9fafb;
  }
  
  .dark .rbc-header {
    background-color: #374151;
    color: #f9fafb;
    border-color: #4b5563;
  }
  
  .dark .rbc-month-view {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-month-row {
    border-color: #4b5563;
  }
  
  .dark .rbc-date-cell {
    color: #f9fafb;
  }
  
  .dark .rbc-off-range-bg {
    background-color: #111827;
  }
  
  .dark .rbc-off-range {
    color: #6b7280;
  }
  
  .dark .rbc-today {
    background-color: #374151;
  }
  
  .dark .rbc-event {
    background-color: #dc2626;
    color: #ffffff;
  }
  
  .dark .rbc-event:hover {
    background-color: #b91c1c;
  }
  
  .dark .rbc-show-more {
    background-color: #374151;
    color: #f9fafb;
  }
  
  .dark .rbc-show-more:hover {
    background-color: #4b5563;
  }
  
  .dark .rbc-time-view {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-time-header {
    background-color: #374151;
    border-color: #4b5563;
  }
  
  .dark .rbc-time-content {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-timeslot-group {
    border-color: #4b5563;
  }
  
  .dark .rbc-time-slot {
    border-color: #4b5563;
  }
  
  .dark .rbc-day-slot {
    border-color: #4b5563;
  }
  
  .dark .rbc-time-gutter {
    background-color: #374151;
    color: #f9fafb;
  }
  
  .dark .rbc-label {
    color: #f9fafb;
  }
  
  .dark .rbc-toolbar {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-toolbar button {
    background-color: #374151;
    color: #f9fafb;
    border-color: #4b5563;
  }
  
  .dark .rbc-toolbar button:hover {
    background-color: #4b5563;
  }
  
  .dark .rbc-toolbar button.rbc-active {
    background-color: #dc2626;
    color: #ffffff;
  }
  
  .dark .rbc-toolbar-label {
    color: #f9fafb;
  }
  
  .dark .rbc-time-header-content {
    border-color: #4b5563;
  }
  
  .dark .rbc-header {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-header {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-content {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-row + .rbc-month-row {
    border-color: #4b5563;
  }
  
  .dark .rbc-date-cell.rbc-off-range {
    color: #6b7280;
  }
  
  .dark .rbc-date-cell.rbc-off-range.rbc-today {
    background-color: #374151;
  }
  
  .dark .rbc-overlay {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-overlay-header {
    background-color: #374151;
    border-color: #4b5563;
  }
  
  .dark .rbc-overlay-content {
    background-color: #1f2937;
  }
  
  .dark .rbc-overlay-content .rbc-event {
    background-color: #dc2626;
    color: #ffffff;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Initialize the calendar localizer
const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom Toolbar component
const CustomToolbar = (toolbar) => {
  const { darkMode } = useTheme();
  
  const goToBack = () => {
    toolbar.onNavigate('PREV');
  };

  const goToNext = () => {
    toolbar.onNavigate('NEXT');
  };

  return (
    <div className="flex items-center justify-between mb-4">
      <button 
        onClick={goToBack}
        className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
        aria-label="Previous month"
      >
        <FaChevronLeft className="text-gray-600 dark:text-gray-300 text-xl" />
      </button>
      <span className="text-2xl font-bold text-gray-800 dark:text-white">
        {toolbar.label}
      </span>
      <button 
        onClick={goToNext}
        className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
        aria-label="Next month"
      >
        <FaChevronRight className="text-gray-600 dark:text-gray-300 text-xl" />
      </button>
    </div>
  );
};

// Helper function to clean HTML content
const cleanHtmlContent = (htmlString) => {
  if (!htmlString) return '';
  
  return htmlString
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

// Helper to clean titles of repeated time prefixes
const cleanTitle = (title) => {
  if (!title) return '';
  return title.replace(/^(\d{1,2}:\d{2}\s*[APap][Mm]\s*-\s*)+/g, '').trim();
};

const Calendar = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const [events, setEvents] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [totalEvents, setTotalEvents] = useState(0);
  const [timeUntilNext, setTimeUntilNext] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);
  const [showNewEventInternalTab, setShowNewEventInternalTab] = useState(true);
  const [showNewEventAccountsTab, setShowNewEventAccountsTab] = useState(false);
  const [newEventAccountSearchTerm, setNewEventAccountSearchTerm] = useState('');
  const [newEventInternalSearchTerm, setNewEventInternalSearchTerm] = useState('');
  const [editingLocalEventId, setEditingLocalEventId] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    location: 'Google Meet',
    isSynced: false,
    syncStatus: 'pending',
    recurrence: {
      frequency: 'none',
      interval: 1,
      until: '',
      byDay: []
    },
    breakthrough: false,
    visibility: 'private',
    attendeeUserIds: [],
    attendees: []
  });
  const [contacts, setContacts] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [isGoogleCalendarConnected, setIsGoogleCalendarConnected] = useState(false);
  const [showGoogleEvents, setShowGoogleEvents] = useState(true);
  const [googleEventsLoading, setGoogleEventsLoading] = useState(false);
  const [googleCalendarError, setGoogleCalendarError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Show more events popup state
  const [showMoreEventsModal, setShowMoreEventsModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);

  // Combine internal and Google Calendar events with deduplication
  const allEvents = showGoogleEvents 
    ? (() => {
        // Enrich Google events with local custom fields when available
        const googleEventsWithLocal = googleEvents.map(ge => {
          const localMatch = events.find(ev => ev.googleEventId === ge.id);
          if (localMatch) {
            return {
              ...ge,
              ...localMatch,
              isGoogleEvent: true
            };
          }
          return { ...ge, isGoogleEvent: true };
        });

        const combinedEvents = [...events, ...googleEventsWithLocal];

        // Merge duplicates, preserving breakthrough/visibility/custom fields
        const byKey = new Map();
        combinedEvents.forEach(ev => {
          const startTime = ev.start instanceof Date ? ev.start.getTime() : new Date(ev.start).getTime();
          const endTime = ev.end instanceof Date ? ev.end.getTime() : new Date(ev.end).getTime();
          const key = `${ev.googleEventId || ev.id || ev.title}-${startTime}-${endTime}`;
          
          if (!byKey.has(key)) {
            byKey.set(key, { ...ev });
          } else {
            const existing = byKey.get(key);
            byKey.set(key, {
              ...existing,
              ...ev,
              breakthrough:
                Boolean(existing.breakthrough) ||
                Boolean(ev.breakthrough) ||
                (ev.title && ev.title.toLowerCase().includes('breakthrough')),
              visibility: ev.visibility || existing.visibility || 'private'
            });
          }
        });
        return Array.from(byKey.values());
      })()
    : events;

  // Inject dark mode styles for calendar
  useEffect(() => {
    const styleId = 'calendar-dark-mode-styles';
    let styleElement = document.getElementById(styleId);
    
    if (darkMode) {
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = calendarDarkModeStyles;
    } else {
      if (styleElement) {
        styleElement.remove();
      }
    }
    
    return () => {
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [darkMode]);

  // Fetch events for the current month view
  const fetchMonthEvents = async (date) => {
    try {
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const eventsQuery = query(
        collection(db, "events"),
        where("startTime", ">=", monthStart),
        where("startTime", "<=", monthEnd),
        orderBy("startTime", "asc")
      );

      const querySnapshot = await getDocs(eventsQuery);
      
      const fetchedEvents = [];

      querySnapshot.forEach((doc) => {
        const eventData = doc.data();
        const startTime = eventData.startTime.toDate();
        fetchedEvents.push({
          id: doc.id,
          title: eventData.title,
          start: startTime,
          end: eventData.endTime.toDate(),
          description: eventData.description,
          ...eventData
        });
      });

      // Filter events on the frontend
      const filteredEvents = fetchedEvents.filter(event => {
        const isPublic = event.visibility === "public";
        const isAttendee = event.attendees?.includes(currentUser.uid);
        const isGroupMember = event.visibility === "group" && event.groupId === currentUser.groupId;
        const isCreator = event.createdBy === currentUser.uid;
        
        return isPublic || isAttendee || isGroupMember || isCreator;
      });

      setEvents(filteredEvents);
      setTotalEvents(filteredEvents.length);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  // Fetch Google Calendar events
  const fetchGoogleCalendarEventsForMonth = async (date) => {
    if (!isGoogleCalendarConnected) {
      console.log("Google Calendar not connected, skipping event fetch");
      return;
    }

    try {
      setGoogleEventsLoading(true);
      console.log("Fetching Google Calendar events for month:", date);
      
      // Check and refresh token only if needed
      const tokenCheckResult = await checkAndRefreshGoogleCalendarToken();
      console.log("Token check result:", tokenCheckResult);
      
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const googleEventsData = await fetchGoogleCalendarEvents(
        currentUser.uid,
        monthStart,
        monthEnd
      );

      console.log("Google Calendar events fetched:", googleEventsData);
      console.log("Number of events:", googleEventsData?.length || 0);
      setGoogleEvents(googleEventsData || []);
      setGoogleCalendarError(''); // Clear any previous errors
    } catch (error) {
      console.error("Error fetching Google Calendar events:", error);
      setGoogleEvents([]);
      
      // If it's a CORS or network error, try to refresh the connection status
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        console.log('CORS or network error detected, rechecking Google Calendar connection...');
        setTimeout(() => {
          checkGoogleCalendarConnection();
        }, 2000);
      }
      
      // If it's a token refresh error, disable Google Calendar
      if (error.message.includes('Refresh token expired') || error.message.includes('needsReauth')) {
        console.log('Google Calendar needs re-authentication, disabling integration...');
        setIsGoogleCalendarConnected(false);
        setShowGoogleEvents(false);
        setGoogleCalendarError('Google Calendar integration needs to be reconnected. Please go to your profile settings to reconnect.');
      }
    } finally {
      setGoogleEventsLoading(false);
    }
  };

  // Retry Google Calendar connection
  const retryGoogleCalendarConnection = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    setGoogleCalendarError('');
    
    try {
      console.log(`Retrying Google Calendar connection (attempt ${retryCount + 1})...`);
      await checkGoogleCalendarConnection();
      
      if (isGoogleCalendarConnected) {
        console.log("Google Calendar connection restored");
        setRetryCount(0);
      } else if (retryCount < 2) {
        // Retry up to 3 times
        setTimeout(() => {
          retryGoogleCalendarConnection();
        }, 2000);
      } else {
        setGoogleCalendarError('Google Calendar connection failed after multiple attempts. Please check your integration settings.');
      }
    } catch (error) {
      console.error("Error during retry:", error);
      setGoogleCalendarError('Failed to reconnect Google Calendar. Please check your integration settings.');
    } finally {
      setIsRetrying(false);
    }
  };

  // Check Google Calendar connection status.
  // Tokens live server-side; the profile only carries the non-secret
  // integrations.googleCalendar.connected / needsReauth flags.
  const checkGoogleCalendarConnection = async () => {
    try {
      const integrationsStatus = await getUserIntegrationsStatus(currentUser.uid);
      const googleCalendar = integrationsStatus?.googleCalendar;

      if (!googleCalendar?.connected) {
        setIsGoogleCalendarConnected(false);
        setShowGoogleEvents(false);
        // A dead grant flips connected off and sets needsReauth; otherwise the
        // user just hasn't connected yet, so no error.
        setGoogleCalendarError(googleCalendar?.needsReauth
          ? 'Google Calendar integration needs to be reconnected. Please go to your profile settings to reconnect.'
          : '');
        return;
      }

      if (!googleCalendar.needsReauth) {
        setIsGoogleCalendarConnected(true);
        setGoogleCalendarError('');
        return;
      }

      // Flagged for re-auth: give the server one chance to refresh the grant
      const refreshSuccess = await checkAndRefreshGoogleCalendarToken();
      if (refreshSuccess) {
        setIsGoogleCalendarConnected(true);
        setGoogleCalendarError('');
      } else {
        setIsGoogleCalendarConnected(false);
        setShowGoogleEvents(false);
        setGoogleCalendarError('Google Calendar token expired and could not be refreshed. Please reconnect your Google Calendar integration.');
      }
    } catch (error) {
      console.error("Error checking Google Calendar connection:", error);
      setIsGoogleCalendarConnected(false);
      setShowGoogleEvents(false);
      setGoogleCalendarError('Error checking Google Calendar connection. Please try reconnecting your Google Calendar integration.');
    }
  };

  // Fetch upcoming events (next 30 days)
  const fetchUpcomingEvents = async () => {
    try {
      const now = new Date();
      const next30Days = addDays(now, 30);

      const upcomingQuery = query(
        collection(db, "events"),
        where("startTime", ">=", now),
        where("startTime", "<=", next30Days),
        orderBy("startTime", "asc")
      );

      const querySnapshot = await getDocs(upcomingQuery);
      const fetchedEvents = [];

      querySnapshot.forEach((doc) => {
        const eventData = doc.data();
        const startTime = eventData.startTime.toDate();
        fetchedEvents.push({
          id: doc.id,
          title: eventData.title,
          start: startTime,
          end: eventData.endTime.toDate(),
          description: eventData.description,
          ...eventData
        });
      });

      // Filter events on the frontend
      const filteredEvents = fetchedEvents.filter(event => {
        return (
          event.visibility === "public" ||
          event.attendees?.includes(currentUser.uid) ||
          (event.visibility === "group" && event.groupId === currentUser.groupId)
        );
      });

      // Set the next event
      if (filteredEvents.length > 0) {
        setNextEvent(filteredEvents[0]);
      }
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    }
  };

  // Fetch clients and internal users on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const accountsRef = collection(db, "accounts");
        const querySnapshot = await getDocs(accountsRef);
        const clientsList = querySnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: 'client',
              name: data.company || data.name || 'Unknown Company',
              email: data.contact?.email || data.email || data.businessEmail || data.primaryEmail || '',
              ...data
            };
          })
          .filter(client => {
            // Only show Clients, not Leads
            const accountType = client.type || client.accountType;
            if (accountType && (accountType.toLowerCase() === 'leads' || accountType.toLowerCase() === 'lead')) {
              return false;
            }
            
            // Filter for active clients only — one lifecycle rule (accountLifecycle.js):
            // not Canceled / Lost / Archived and not deleted.
            return isActiveClientAccount(client);
          });
        console.log('Fetched active clients:', clientsList); // Debug log
        console.log('Active client email addresses:', clientsList.map(client => ({ name: client.name, email: client.email }))); // Debug email addresses
        setContacts(clientsList);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };

    const fetchInternalUsers = async () => {
      try {
        const internalUsersRef = collection(db, "internal_users");
        const querySnapshot = await getDocs(internalUsersRef);
        const usersList = querySnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Try different possible name fields
          let displayName = 'Unknown User';
          if (data.firstName && data.lastName) {
            displayName = `${data.firstName} ${data.lastName}`;
          } else if (data.displayName) {
            displayName = data.displayName;
          } else if (data.name) {
            displayName = data.name;
          } else if (data.email) {
            displayName = data.email;
          }
          
          console.log('Processed displayName:', displayName); // Debug log
          
          return {
            id: doc.id,
            type: 'internal',
            displayName: displayName,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            name: data.name,
            ...data
          };
        });
        setInternalUsers(usersList);
      } catch (error) {
        console.error("Error fetching internal users:", error);
      }
    };

    fetchClients();
    fetchInternalUsers();
  }, []);




  // Ask the server to refresh the Google Calendar grant when the profile is
  // flagged needsReauth. The server keeps the token; nothing comes back but
  // success/failure. Otherwise the calendar functions refresh on their own.
  const checkAndRefreshGoogleCalendarToken = async () => {
    try {
      const integrationsStatus = await getUserIntegrationsStatus(currentUser.uid);
      const googleCalendar = integrationsStatus?.googleCalendar;

      if (!googleCalendar?.connected) {
        return false;
      }

      if (!googleCalendar.needsReauth) {
        return true;
      }

      const response = await fetch('https://us-central1-bba-portal-894c2.cloudfunctions.net/refreshGoogleCalendarToken', {
        method: 'POST',
        headers: {
          ...(await idTokenHeader()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUser.uid
        })
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        return true;
      }

      console.error("Google Calendar token refresh failed:", result.error || response.status);
      if (result.needsReauth) {
        setIsGoogleCalendarConnected(false);
        setShowGoogleEvents(false);
        setGoogleCalendarError('Your Google Calendar connection has expired. Please reconnect your Google Calendar integration in your profile settings to continue syncing events.');
      }
      return false;
    } catch (error) {
      console.error("Error refreshing Google Calendar token:", error);
      return false;
    }
  };

  useEffect(() => {
    fetchMonthEvents(currentDate);
    fetchUpcomingEvents();
    checkGoogleCalendarConnection();
  }, [currentDate, currentUser.uid]);

  useEffect(() => {
    if (isGoogleCalendarConnected) {
      fetchGoogleCalendarEventsForMonth(currentDate);
    }
  }, [currentDate, isGoogleCalendarConnected]);


  useEffect(() => {
    // Update countdown timer
    const updateCountdown = () => {
      if (nextEvent) {
        const diff = nextEvent.start - new Date();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        setTimeUntilNext(`${days}d ${hours}h`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [nextEvent]);

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
    fetchMonthEvents(newDate);
    if (isGoogleCalendarConnected) {
      fetchGoogleCalendarEventsForMonth(newDate);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (e.target.disabled) return;
    e.target.disabled = true;
    
    // Validate attendees have email addresses
    const attendeesWithoutEmail = newEvent.attendees.filter(attendee => !attendee.email || attendee.email.trim() === '');
    if (attendeesWithoutEmail.length > 0) {
      alert('Some selected attendees do not have email addresses. Please remove them or add email addresses to their profiles.');
      e.target.disabled = false;
      return;
    }
    
    // Handle editing existing events
    if (isEditingEvent && editingEventId) {
      try {
        console.log('Updating existing event:', editingEventId);
        
        // Find the original event to check if it's a Google Calendar event
        const originalEvent = [...events, ...googleEvents].find(event => event.id === editingEventId);
        
        const { doc, updateDoc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        // Prepare event data with all fields including breakthrough and visibility
        // Breakthrough is a boolean - ensure it's properly set
        const breakthroughValue = Boolean(newEvent.breakthrough);
        console.log('Breakthrough value before update:', newEvent.breakthrough, 'converted to:', breakthroughValue);
        
        const eventData = {
          title: cleanTitle(newEvent.title),
          description: newEvent.description || '',
          startTime: new Date(newEvent.startTime),
          endTime: new Date(newEvent.endTime),
          location: newEvent.location || 'Google Meet',
          recurrence: newEvent.recurrence || {
            frequency: 'none',
            interval: 1,
            until: '',
            byDay: []
          },
          breakthrough: breakthroughValue,
          visibility: newEvent.visibility || 'private',
          attendeeUserIds: newEvent.attendeeUserIds || [],
          attendees: newEvent.attendees.filter(attendee => attendee.email && attendee.email.trim() !== '').map(attendee => ({
            userId: attendee.id,
            userName: attendee.type === 'internal' ? attendee.displayName : attendee.name,
            email: attendee.email.trim(),
            role: attendee.type,
            responseStatus: 'pending'
          }))
        };
        console.log('Event data being saved:', { breakthrough: eventData.breakthrough, visibility: eventData.visibility });
        
        // Always update the local Firestore event first (for breakthrough and other portal fields)
        // This is independent of Google Calendar - breakthrough is a portal organization field
        let localEventDocId = editingLocalEventId || editingEventId;
        
        if (originalEvent && originalEvent.isGoogleEvent) {
          // For Google Calendar events, find the corresponding local event
          try {
            const localEventQuery = query(
              collection(db, "events"),
              where("googleEventId", "==", editingEventId)
            );
            const localEventSnapshot = await getDocs(localEventQuery);
            
            if (!localEventSnapshot.empty) {
              localEventDocId = localEventSnapshot.docs[0].id;
              console.log('Found local event for Google Calendar event:', localEventDocId);
            } else {
              // If no local event exists, we'll create one after updating Google Calendar
              console.log('No local event found for Google Calendar event, will create one');
            }
          } catch (queryError) {
            console.error('Error querying for local event:', queryError);
          }
          
          // Update Google Calendar event (separate from portal fields)
          console.log('Updating Google Calendar event...');
          const googleEventData = {
            title: cleanTitle(newEvent.title),
            description: newEvent.description,
            startTime: new Date(newEvent.startTime).toISOString(),
            endTime: new Date(newEvent.endTime).toISOString(),
            location: newEvent.location,
            attendees: newEvent.attendees.map(attendee => ({
              email: attendee.email,
              userName: attendee.userName
            }))
          };
          
          try {
            const updatedGoogleEvent = await updateGoogleCalendarEvent(currentUser.uid, editingEventId, googleEventData);
            console.log('Google Calendar event updated successfully:', updatedGoogleEvent.id);
          } catch (googleError) {
            console.error('Error updating Google Calendar event:', googleError);
            // Continue to update local event even if Google Calendar update fails
          }
        }
        
        // Always update/create the local Firestore event for portal fields (breakthrough, visibility, etc.)
        console.log('Updating local Firestore event for portal fields...', localEventDocId);
        try {
          // Check if the document exists
          const eventDocRef = doc(db, "events", localEventDocId);
          const eventDocSnap = await getDoc(eventDocRef);
          
          if (eventDocSnap.exists()) {
            // Update existing local event
            console.log('Updating document:', localEventDocId, 'with data:', JSON.stringify(eventData, null, 2));
            await updateDoc(eventDocRef, eventData);
            console.log('Local event updated successfully. Breakthrough saved as:', eventData.breakthrough);
            
            // Verify the update
            const updatedDoc = await getDoc(eventDocRef);
            console.log('Verified breakthrough in database:', updatedDoc.data()?.breakthrough);
            
            // Update local state
            setEvents(prevEvents => 
              prevEvents.map(event => 
                event.id === localEventDocId 
                  ? { ...event, ...eventData, start: new Date(eventData.startTime), end: new Date(eventData.endTime) }
                  : event
              )
            );
          } else {
            // Create new local event if it doesn't exist (for Google Calendar events)
            const newEventData = {
              ...eventData,
              createdAt: serverTimestamp(),
              createdBy: currentUser.uid,
              googleEventId: originalEvent && originalEvent.isGoogleEvent ? editingEventId : '',
              isSynced: originalEvent && originalEvent.isGoogleEvent ? true : false,
              syncStatus: originalEvent && originalEvent.isGoogleEvent ? 'synced' : 'pending'
            };
            await addDoc(collection(db, "events"), newEventData);
            console.log('Created new local event with breakthrough:', newEventData.breakthrough);
          }
        } catch (localError) {
          console.error('Error updating local event:', localError);
          throw localError;
        }
        
        // Reset editing state
        setIsEditingEvent(false);
        setEditingEventId(null);
        setEditingLocalEventId(null);
        setShowModal(false);
        
        // Reset form
        setNewEvent({
          title: '',
          description: '',
          startTime: '',
          endTime: '',
          location: 'Google Meet',
          isSynced: false,
          syncStatus: 'pending',
          recurrence: {
            frequency: 'none',
            interval: 1,
            until: '',
            byDay: []
          },
          breakthrough: false,
          visibility: 'private',
          attendeeUserIds: [],
          attendees: []
        });
        
        console.log('Event update completed');
        return;
        
      } catch (error) {
        console.error("Error updating event:", error);
        alert('Error updating event: ' + error.message);
        e.target.disabled = false;
        return;
      }
    }
    
    try {
      let googleEventId = '';
      let googleCalendarId = '';
      let googleMeetLink = '';
      let isSynced = false;
      let syncStatus = 'pending';
      let syncedAt = null;

      // Create Google Calendar event if connected
      if (isGoogleCalendarConnected) {
        try {
          console.log('Creating Google Calendar event...');
          const googleEventData = {
            title: newEvent.title,
            description: newEvent.description,
            startTime: new Date(newEvent.startTime).toISOString(),
            endTime: new Date(newEvent.endTime).toISOString(),
            location: newEvent.location, // Keep the original location value for Google Meet detection
            attendees: newEvent.attendees.map(attendee => ({
              email: attendee.email,
              userName: attendee.userName
            }))
          };

          const googleEvent = await createGoogleCalendarEvent(currentUser.uid, googleEventData);
          
          if (googleEvent) {
            googleEventId = googleEvent.id;
            googleCalendarId = googleEvent.organizer?.email || 'primary';
            googleMeetLink = googleEvent.hangoutLink || googleEvent.conferenceData?.entryPoints?.[0]?.uri || '';
            isSynced = true;
            syncStatus = 'synced';
            syncedAt = new Date();
            console.log('Google Calendar event created successfully:', googleEvent.id);
          }
        } catch (googleError) {
          console.error('Error creating Google Calendar event:', googleError);
          
          // Check if it's a Google Meet scope issue
          if (googleError.message.includes('Google Calendar integration needs to be reconnected')) {
            alert('Google Meet Integration Issue: Your Google Calendar integration needs to be reconnected to enable Google Meet functionality. Please go to your CRM Profile → Integrations and reconnect Google Calendar.');
          }
          
          // Continue with local event creation even if Google Calendar fails
          syncStatus = 'failed';
        }
      }

      // Always create a local event to store custom fields like breakthrough and visibility
      // Even if Google Calendar is connected, we need local storage for these fields
        const eventData = {
          title: cleanTitle(newEvent.title),
        description: newEvent.description || '',
        startTime: new Date(newEvent.startTime),
        endTime: new Date(newEvent.endTime),
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        location: newEvent.location || 'Google Meet',
        recurrence: newEvent.recurrence || {
          frequency: 'none',
          interval: 1,
          until: '',
          byDay: []
        },
          breakthrough: newEvent.breakthrough === true || newEvent.breakthrough === 'true' || newEvent.breakthrough === 'yes',
        visibility: newEvent.visibility || 'private',
        isSynced: isSynced,
        syncStatus: syncStatus,
        googleEventId: googleEventId || '',
        googleCalendarId: googleCalendarId || '',
        googleMeetLink: googleMeetLink || '',
        syncedAt: syncedAt,
        attendeeUserIds: newEvent.attendeeUserIds || [],
        attendees: newEvent.attendees.filter(attendee => attendee.email && attendee.email.trim() !== '').map(attendee => ({
          userId: attendee.id,
          userName: attendee.type === 'internal' ? attendee.displayName : attendee.name,
          email: attendee.email.trim(),
          role: attendee.type,
          responseStatus: 'pending'
        }))
      };

      const docRef = await addDoc(collection(db, "events"), eventData);
      console.log('Local event created successfully with ID:', docRef.id);
      
      // Add the new event to local state
      const newEventForState = {
        id: docRef.id,
        title: eventData.title,
        start: eventData.startTime,
        end: eventData.endTime,
        description: eventData.description,
        ...eventData
      };
      setEvents(prevEvents => [...prevEvents, newEventForState]);
      
      // If Google Calendar is connected, also refresh Google events
      if (isGoogleCalendarConnected) {
        await fetchGoogleCalendarEventsForMonth(currentDate);
      }
      
      setShowModal(false);
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        marked: 'no',
        location: 'Google Meet',
        isSynced: false,
        syncStatus: 'pending',
        recurrence: {
          frequency: 'none',
          interval: 1,
          until: '',
          byDay: []
        },
        breakthrough: false,
        visibility: 'private',
        attendeeUserIds: [],
        attendees: []
      });
      
      // Event created successfully - no need for alert
      console.log('Event creation completed. Google Calendar connected:', isGoogleCalendarConnected);
      
    } catch (error) {
      console.error("Error creating event:", error);
      alert('Error creating event: ' + error.message);
    } finally {
      // Re-enable the button
      e.target.disabled = false;
    }
  };



  const toggleEventAttendee = (user) => {
    const isSelected = newEvent.attendees.find(a => a.userId === user.id);
    if (isSelected) {
      setNewEvent({
        ...newEvent,
        attendeeUserIds: newEvent.attendeeUserIds.filter(id => id !== user.id),
        attendees: newEvent.attendees.filter(attendee => attendee.userId !== user.id)
      });
    } else {
      setNewEvent({
        ...newEvent,
        attendeeUserIds: [...newEvent.attendeeUserIds, user.id],
        attendees: [...newEvent.attendees, {
          userId: user.id,
          userName: user.displayName || user.name,
          email: user.email,
          role: user.type || 'internal',
          profileImage: user.profileImage || null,
          googleAttendeeId: '',
          responseStatus: 'pending',
          synced: false
        }]
      });
    }
  };

  const addAllInternalUsers = () => {
    const allInternalUsers = internalUsers.filter(user => 
      !newEvent.attendees.some(a => a.userId === user.id)
    );
    
    const newAttendees = allInternalUsers.map(user => ({
      userId: user.id,
      userName: user.displayName || user.name,
      email: user.email,
      role: 'internal',
      profileImage: user.profileImage || null,
      googleAttendeeId: '',
      responseStatus: 'pending',
      synced: false
    }));
    
    setNewEvent({
      ...newEvent,
      attendeeUserIds: [...newEvent.attendeeUserIds, ...allInternalUsers.map(u => u.id)],
      attendees: [...newEvent.attendees, ...newAttendees]
    });
  };

  const addAllClients = () => {
    const allClients = contacts.filter(client => 
      !newEvent.attendees.some(a => a.userId === client.id) &&
      (client.contact?.email || client.email || client.businessEmail || '').trim() !== ''
    );
    
    const newAttendees = allClients.map(client => ({
      userId: client.id,
      userName: client.name,
      email: client.contact?.email || client.email || client.businessEmail || '',
      role: 'client',
      googleAttendeeId: '',
      responseStatus: 'pending',
      synced: false
    }));
    
    setNewEvent({
      ...newEvent,
      attendeeUserIds: [...newEvent.attendeeUserIds, ...allClients.map(c => c.id)],
      attendees: [...newEvent.attendees, ...newAttendees]
    });
  };

  const clearAllAttendees = () => {
    setNewEvent({
      ...newEvent,
      attendeeUserIds: [],
      attendees: []
    });
  };

  // Helper to enrich Google events with local portal fields
  const enrichEventWithLocalData = async (event) => {
    if (!event?.isGoogleEvent) return event;
    try {
      const localEventQuery = query(
        collection(db, "events"),
        where("googleEventId", "==", event.id)
      );
      const localEventSnapshot = await getDocs(localEventQuery);
      if (!localEventSnapshot.empty) {
        const localDoc = localEventSnapshot.docs[0];
        const localData = localDoc.data();
        return {
          ...event,
          ...localData,
          localEventId: localDoc.id,
        };
      }
    } catch (err) {
      console.error("Error enriching Google event with local data:", err);
    }
    return event;
  };

  const handleEventClick = (event) => {
    (async () => {
      const enriched = await enrichEventWithLocalData(event);
      setSelectedEvent(enriched);
      setShowEventDetailsModal(true);
    })();
  };

  const handleShowMore = (events, date) => {
    setSelectedDate(date);
    setSelectedDateEvents(events);
    setShowMoreEventsModal(true);
  };

  const handleEditEvent = () => {
    setNewEvent({
      title: cleanTitle(selectedEvent.title),
      description: selectedEvent.description,
      startTime: format(selectedEvent.start, "yyyy-MM-dd'T'HH:mm"),
      endTime: format(selectedEvent.end, "yyyy-MM-dd'T'HH:mm"),
      location: selectedEvent.location,
      googleMeetLink: selectedEvent.googleMeetLink || '',
      isSynced: selectedEvent.isSynced,
      syncStatus: selectedEvent.syncStatus,
      recurrence: selectedEvent.recurrence || {
        frequency: 'none',
        interval: 1,
        until: '',
        byDay: []
      },
      breakthrough: selectedEvent.breakthrough || false,
      visibility: selectedEvent.visibility || 'private',
      attendeeUserIds: selectedEvent.attendeeUserIds || [],
      attendees: selectedEvent.attendees || []
    });
    
    // Track local event id for Google Calendar events
    if (selectedEvent.localEventId) {
      setEditingLocalEventId(selectedEvent.localEventId);
    } else {
      setEditingLocalEventId(null);
    }
    
    // Set editing state
    setIsEditingEvent(true);
    setEditingEventId(selectedEvent.id);
    
    // Close the details modal and immediately open the edit modal
    // This prevents the brief blank moment
    setShowEventDetailsModal(false);
    setShowModal(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || !selectedEvent.id) return;
    
    try {
      await deleteDoc(doc(db, "events", selectedEvent.id));
      setShowEventDetailsModal(false);
      // Refresh events
      fetchMonthEvents(currentDate);
      fetchUpcomingEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  return (
    <div className={`${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-6'} min-h-screen relative`}>
      <PortalBackground />
      <div className="relative">
      <div className={`flex justify-center items-center ${isMobile ? 'mb-4' : 'mb-6'} relative`}>
        <h1 className={`${isMobile ? 'text-xl' : isTablet ? 'text-2xl' : 'text-3xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Calendar</h1>
        <div className="absolute right-0 flex items-center gap-4">
          {/* Google Calendar Toggle */}
          {(isGoogleCalendarConnected || googleCalendarError) && (
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showGoogleEvents}
                  onChange={(e) => setShowGoogleEvents(e.target.checked)}
                  className="sr-only peer"
                  disabled={!isGoogleCalendarConnected}
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 ${!isGoogleCalendarConnected ? 'opacity-50' : ''}`}></div>
              </label>
              <span className={`text-sm ${isGoogleCalendarConnected ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                Google Calendar
              </span>
              {googleEventsLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              )}
            </div>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-200"
            title="Add Event"
          >
            <FaPlus />
          </button>
        </div>
      </div>

      {/* Google Calendar Error Message */}
      {googleCalendarError && (
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Google Calendar Integration Issue
              </h3>
              <div className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                <p>{googleCalendarError}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => navigate('/crm/profile')}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-yellow-800 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-800 dark:text-yellow-100 dark:hover:bg-yellow-700 transition-colors"
                  >
                    Go to Profile Settings
                  </button>
                  {retryCount < 3 && (
                    <button
                      onClick={retryGoogleCalendarConnection}
                      disabled={isRetrying}
                      className="inline-flex items-center px-3 py-1 border border-yellow-300 text-xs font-medium rounded text-yellow-800 bg-transparent hover:bg-yellow-50 dark:border-yellow-600 dark:text-yellow-200 dark:hover:bg-yellow-800/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRetrying ? 'Retrying...' : 'Retry Connection'}
                    </button>
                  )}
                </div>
                {retryCount > 0 && (
                  <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                    Retry attempt {retryCount} of 3
                  </p>
                )}
              </div>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setGoogleCalendarError('')}
                className="text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-200 transition-colors"
                title="Dismiss"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">

        <BigCalendar
          localizer={localizer}
          events={allEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 'calc(100vh - 200px)' }}
          view="month"
          onNavigate={handleNavigate}
          date={currentDate}
          onDrillDown={handleNavigate}
          views={['month']}
          tooltipAccessor={(event) => `${event.title}\n${event.description}`}
          components={{
            toolbar: CustomToolbar
          }}
          min={new Date(2024, 0, 1, 7, 0)}
          max={new Date(2024, 0, 1, 18, 0)}
          onSelectEvent={handleEventClick}
          onShowMore={handleShowMore}
          className={darkMode ? 'dark' : ''}
          eventPropGetter={(event) => {
            const isBreakthrough =
              event.breakthrough ||
              (event.title && event.title.toLowerCase().includes('breakthrough'));

            // Breakthrough events are always red (trumps Google color)
            if (isBreakthrough) {
              return {
                style: {
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: '1px solid #b91c1c',
                  borderRadius: '4px'
                }
              };
            }
            // Style Google Calendar events differently
            if (event.isGoogleEvent) {
              return {
                style: {
                  backgroundColor: '#4285f4',
                  color: '#ffffff',
                  border: '1px solid #1a73e8',
                  borderRadius: '4px'
                }
              };
            }
            // Default styling for internal events
            return {
              style: {
                backgroundColor: '#dc2626',
                color: '#ffffff',
                border: '1px solid #b91c1c',
                borderRadius: '4px'
              }
            };
          }}
        />
      </div>

      {/* New Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-200" style={{ animation: 'fadeIn 0.2s ease-in-out' }}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-2xl transition-transform duration-200">
            <div className="flex justify-between items-center mb-4 relative">
              <div className="w-8"></div> {/* Spacer for balance */}
              <h2 className="text-xl font-bold absolute left-1/2 transform -translate-x-1/2 dark:text-white">
                {isEditingEvent ? 'Edit Event' : 'Add Event'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setIsEditingEvent(false);
                  setEditingEventId(null);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                    rows="3"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Time
                    </label>
                    <input
                      type="datetime-local"
                      value={newEvent.startTime}
                      onChange={(e) => {
                        const startTime = e.target.value;
                        setNewEvent(prev => {
                          // Always auto-populate end time when start time changes
                          let newEndTime = prev.endTime;
                          if (startTime) {
                            // Parse the datetime-local value directly without timezone conversion
                            const [datePart, timePart] = startTime.split('T');
                            const [year, month, day] = datePart.split('-').map(Number);
                            const [hours, minutes] = timePart.split(':').map(Number);
                            
                            // Create date in local timezone
                            const startDate = new Date(year, month - 1, day, hours, minutes);
                            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
                            
                            // Format back to datetime-local format
                            const endYear = endDate.getFullYear();
                            const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
                            const endDay = String(endDate.getDate()).padStart(2, '0');
                            const endHours = String(endDate.getHours()).padStart(2, '0');
                            const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
                            
                            newEndTime = `${endYear}-${endMonth}-${endDay}T${endHours}:${endMinutes}`;
                          }
                          
                          return {
                            ...prev,
                            startTime: startTime,
                            endTime: newEndTime
                          };
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Time
                    </label>
                    <input
                      type="datetime-local"
                      value={newEvent.endTime}
                      onChange={(e) => setNewEvent({...newEvent, endTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Guests
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssigneesModal(true);
                      setShowNewEventInternalTab(true);
                      setShowNewEventAccountsTab(false);
                      setNewEventAccountSearchTerm('');
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>
                        {newEvent.attendees.length > 0 
                          ? newEvent.attendees.map(a => a.userName).join(', ')
                          : 'Select guests'
                        }
                      </span>
                    </div>
                    <FaChevronRight className="text-gray-500 dark:text-gray-400" />
                  </button>

                  {/* Selected Guests Avatars */}
                  {newEvent.attendees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newEvent.attendees.map(attendee => (
                        <div key={attendee.userId} className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 overflow-hidden relative">
                          {attendee.profileImage ? (
                            <img
                              src={attendee.profileImage}
                              alt={attendee.userName || attendee.displayName || 'User'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Show initials if image fails to load
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                const initialsSpan = parent.querySelector('.initials-fallback');
                                if (initialsSpan) initialsSpan.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          {!attendee.profileImage && (
                            <span>
                              {(attendee.userName || attendee.displayName || attendee.email || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                          {attendee.profileImage && (
                            <span className="initials-fallback absolute inset-0 flex items-center justify-center hidden">
                              {(attendee.userName || attendee.displayName || attendee.email || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <select
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="Google Meet">Google Meet</option>
                    <option value="Phone Call">Phone Call</option>
                    <option value="In-Person">In-Person</option>
                  </select>
                  {newEvent.location === 'In-Person' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={newEvent.address}
                        onChange={(e) => setNewEvent({...newEvent, address: e.target.value})}
                        placeholder="Enter meeting address"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  )}
                </div>

                {/* Recurrence Options */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Recurrence
                  </label>
                  <select
                    value={newEvent.recurrence.frequency}
                    onChange={(e) => setNewEvent({
                      ...newEvent,
                      recurrence: {
                        ...newEvent.recurrence,
                        frequency: e.target.value,
                        byDay: e.target.value === 'none' ? [] : newEvent.recurrence.byDay
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="none">No Recurrence</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>

                  {newEvent.recurrence.frequency !== 'none' && (
                    <div className="mt-2 space-y-2">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                          Repeat Every
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={newEvent.recurrence.interval}
                          onChange={(e) => setNewEvent({
                            ...newEvent,
                            recurrence: {
                              ...newEvent.recurrence,
                              interval: parseInt(e.target.value) || 1
                            }
                          })}
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                        />
                        <span className="ml-2 text-gray-600">
                          {newEvent.recurrence.frequency === 'daily' ? 'days' :
                           newEvent.recurrence.frequency === 'weekly' ? 'weeks' : 'months'}
                        </span>
                      </div>

                      {newEvent.recurrence.frequency === 'weekly' && (
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                            Repeat On
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].map((day) => (
                              <button
                                key={day}
                                type="button"
                                onClick={() => {
                                  const byDay = newEvent.recurrence.byDay.includes(day)
                                    ? newEvent.recurrence.byDay.filter(d => d !== day)
                                    : [...newEvent.recurrence.byDay, day];
                                  setNewEvent({
                                    ...newEvent,
                                    recurrence: {
                                      ...newEvent.recurrence,
                                      byDay
                                    }
                                  });
                                }}
                                className={`px-3 py-1 rounded-md text-sm ${
                                  newEvent.recurrence.byDay.includes(day)
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                                }`}
                              >
                                {day}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={newEvent.recurrence.until ? format(new Date(newEvent.recurrence.until), 'yyyy-MM-dd') : ''}
                          onChange={(e) => setNewEvent({
                            ...newEvent,
                            recurrence: {
                              ...newEvent.recurrence,
                              until: e.target.value ? new Date(e.target.value).toISOString() : ''
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Breakthrough Option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Breakthrough
                  </label>
                  <select
                    value={newEvent.breakthrough ? 'yes' : 'no'}
                    onChange={(e) => setNewEvent({
                      ...newEvent,
                      breakthrough: e.target.value === 'yes'
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {isEditingEvent ? 'Update Event' : 'Create Event'}
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Assignees Selection Modal */}
      {showAssigneesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4 relative">
              <div className="w-8"></div> {/* Spacer for balance */}
              <h2 className="text-xl font-bold absolute left-1/2 transform -translate-x-1/2 dark:text-white">Select Guests</h2>
              <button
                onClick={() => setShowAssigneesModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <FaTimes />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b dark:border-gray-700 mb-4">
              <button
                onClick={() => {
                  setShowNewEventInternalTab(true);
                  setShowNewEventAccountsTab(false);
                  setNewEventInternalSearchTerm('');
                }}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  showNewEventInternalTab 
                    ? 'border-b-2 border-red-500 text-red-500' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Internal Users
              </button>
              <button
                onClick={() => {
                  setShowNewEventInternalTab(false);
                  setShowNewEventAccountsTab(true);
                  setNewEventAccountSearchTerm('');
                }}
                className={`flex-1 px-4 py-2 text-sm font-medium ${
                  showNewEventAccountsTab 
                    ? 'border-b-2 border-red-500 text-red-500' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Clients
              </button>
            </div>
            
            {/* Add All Buttons */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
              {newEvent.attendees.length > 0 && (
                <button
                  onClick={clearAllAttendees}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Clear All
                </button>
              )}
              </div>
              {newEvent.attendees.length > 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {newEvent.attendees.length} attendee{newEvent.attendees.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
            
            {/* Internal Users Tab */}
            {showNewEventInternalTab && (
              <div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Search internal users..."
                    value={newEventInternalSearchTerm}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    onChange={(e) => {
                      setNewEventInternalSearchTerm(e.target.value);
                    }}
                  />
                  <button
                    onClick={addAllInternalUsers}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    Add All Internal Users
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {internalUsers
                    .filter(user => {
                      const matchesSearch = user.displayName?.toLowerCase().includes(newEventInternalSearchTerm.toLowerCase()) ||
                                         user.email?.toLowerCase().includes(newEventInternalSearchTerm.toLowerCase());
                      return matchesSearch;
                    })
                    .map(user => (
                      <div
                        key={user.id}
                        onClick={() => toggleEventAttendee(user)}
                        className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-xl border border-gray-200 dark:border-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={newEvent.attendees.some(a => a.userId === user.id)}
                          onChange={() => {}}
                          className="mr-3 w-4 h-4"
                        />
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3 overflow-hidden relative">
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={user.displayName || 'User'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Show initials if image fails to load
                                e.target.style.display = 'none';
                                const parent = e.target.parentElement;
                                const initialsSpan = parent.querySelector('.initials-fallback');
                                if (initialsSpan) initialsSpan.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          {!user.profileImage && (
                            <span>
                              {user.displayName?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                          {user.profileImage && (
                            <span className="initials-fallback absolute inset-0 flex items-center justify-center hidden">
                              {user.displayName?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                        </div>
                        <span className="text-gray-900 dark:text-white font-medium">{user.displayName}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Clients Tab */}
            {showNewEventAccountsTab && (
              <div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={newEventAccountSearchTerm}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    onChange={(e) => {
                      setNewEventAccountSearchTerm(e.target.value);
                    }}
                  />
                  <button
                    onClick={addAllClients}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors whitespace-nowrap"
                  >
                    Add All Clients
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {contacts
                    .filter(client => {
                      const matchesSearch = client.name?.toLowerCase().includes(newEventAccountSearchTerm.toLowerCase());
                      return matchesSearch;
                    })
                    .map((client) => (
                      <div
                        key={client.id}
                        onClick={() => toggleEventAttendee({
                          id: client.id,
                          displayName: client.name,
                          email: client.contact?.email || client.email || client.businessEmail || '',
                          type: 'client'
                        })}
                        className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-xl border border-gray-200 dark:border-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={newEvent.attendees.some(a => a.userId === client.id)}
                          onChange={() => {}}
                          className="mr-3 w-4 h-4"
                        />
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3">
                          {client.name?.charAt(0).toUpperCase() || 'C'}
                        </div>
                        <div className="flex-1">
                          <span className="text-gray-900 dark:text-white font-medium">{client.name}</span>
                          {(!client.email || client.email.trim() === '') && (
                            <span className="ml-2 text-xs text-red-500">⚠️ No email</span>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Modal Footer */}
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() => {
                  setNewEvent({
                    ...newEvent,
                    visibility: newEvent.visibility === 'public' ? 'private' : 'public'
                  });
                }}
                className={`px-4 py-2 rounded-md transition-colors ${
                  newEvent.visibility === 'public'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                }`}
              >
                {newEvent.visibility === 'public' ? '✓ Public' : 'Make Public'}
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowAssigneesModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowAssigneesModal(false)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-200" style={{ animation: 'fadeIn 0.2s ease-in-out' }}>
          <div className={`rounded-2xl p-6 w-full max-w-md bg-white dark:bg-gray-900 transition-transform duration-200`}>
            <div className="flex justify-center items-center mb-4 relative">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedEvent.title}</h2>
            </div>
            <div className="space-y-4">
              {selectedEvent.description && (
                <div>
                  <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</h3>
                  <div className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="text-sm leading-relaxed max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                      {cleanHtmlContent(selectedEvent.description)}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date & Time</h3>
                <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {format(selectedEvent.start, 'PPP')}
                  <br />
                  {format(selectedEvent.start, 'h:mm a')} - {format(selectedEvent.end, 'h:mm a')}
                </p>
              </div>
              <div>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</h3>
                <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedEvent.location}</p>
              {selectedEvent.breakthrough && (
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
                    🚀 Breakthrough
                  </span>
                </div>
              )}
              {selectedEvent.location === 'Google Meet' && selectedEvent.googleMeetLink && selectedEvent.googleMeetLink !== 'pending' && (
                  <p className="mt-1">
                    <a href={selectedEvent.googleMeetLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                      Join Google Meet
                    </a>
                  </p>
                )}
                {selectedEvent.location === 'In-Person' && selectedEvent.address && (
                  <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{selectedEvent.address}</p>
                )}
                {selectedEvent.isGoogleEvent && selectedEvent.hangoutLink && (
                  <p className="mt-1">
                    <a href={selectedEvent.hangoutLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                      Join Google Meet
                    </a>
                  </p>
                )}
              </div>

              {selectedEvent.recurrence && selectedEvent.recurrence.frequency !== 'none' && (
                <div>
                  <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Recurrence</h3>
                  <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedEvent.recurrence.frequency.charAt(0).toUpperCase() + selectedEvent.recurrence.frequency.slice(1)} 
                    {selectedEvent.recurrence.interval > 1 ? ` every ${selectedEvent.recurrence.interval} ${selectedEvent.recurrence.frequency}` : ''}
                    {selectedEvent.recurrence.byDay && selectedEvent.recurrence.byDay.length > 0 && (
                      <> on {selectedEvent.recurrence.byDay.join(', ')}</>
                    )}
                    {selectedEvent.recurrence.until && (
                      <> until {format(new Date(selectedEvent.recurrence.until), 'PPP')}</>
                    )}
                  </p>
                </div>
              )}



              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Attendees</h3>
                  <ul className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedEvent.attendees.map((attendee, index) => {
                      const isOrganizer = selectedEvent.isGoogleEvent && 
                        selectedEvent.organizer && 
                        attendee.email === selectedEvent.organizer.email;
                      
                      return (
                        <li key={attendee.userId || attendee.email || index} className="list-disc list-inside">
                          {selectedEvent.isGoogleEvent 
                            ? (attendee.displayName || attendee.email || 'Unknown')
                            : attendee.userName
                          }
                          {isOrganizer && (
                            <span className="ml-2 text-yellow-500">★</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <div className="flex justify-between items-center mt-6">
                <div>
                  {selectedEvent.isGoogleEvent && selectedEvent.htmlLink && (
                    <a href={selectedEvent.htmlLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-sm">
                      View in Google Calendar
                    </a>
                  )}
                </div>
                <div className="flex space-x-3">
                  {!selectedEvent.isGoogleEvent && (
                    <button
                      onClick={handleDeleteEvent}
                      className="px-4 py-2 text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => setShowEventDetailsModal(false)}
                    className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleEditEvent}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Edit Event
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show More Events Modal */}
      {showMoreEventsModal && selectedDate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                Events for {format(selectedDate, 'MMMM d, yyyy')}
              </h2>
              <button
                onClick={() => setShowMoreEventsModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="space-y-3">
              {selectedDateEvents.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  No events for this day
                </p>
              ) : (
                selectedDateEvents.map((event, index) => (
                  <div
                    key={event.id || index}
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowMoreEventsModal(false);
                      setShowEventDetailsModal(true);
                    }}
                    className="p-3 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white text-sm">
                          {event.title}
                        </h3>
                        {event.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                        <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {format(event.start, 'h:mm a')} - {format(event.end, 'h:mm a')}
                          </span>
                          {event.location && (
                            <span className="ml-2">• {event.location}</span>
                          )}
                        </div>
                      </div>
                      {event.isGoogleEvent && (
                        <div className="ml-2 w-2 h-2 bg-blue-500 rounded-full" title="Google Calendar Event"></div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Calendar;